import {
  PDF_FONT_SIZE,
  ReportSectionItemTable,
  type ReportTableProps,
  type TextCellType,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { LedgerPaymentSectionProps } from '@/features/mold/models/ledger';
import {
  LEDGER_BG_COLOR,
  LEDGER_BORDER,
  formatBidRate,
  getEmptySectionTableDataByHeader,
} from '@/features/mold/utils';
import { centerTextStyle } from '@/features/mold/utils/pdf/pdf-utils';
import {
  getContractAmountOrExpendituresTitle,
  getContractName,
  paymentNumberConverter,
  paymentValueConverter,
} from '@/features/project/_utils/payment';
import type {
  PaymentModel,
  PaymentType,
} from '@/features/project/projects/_models/payment';
import type { PaymentGroupModel } from '@/features/project/projects/_models/payment-group';
import { FALLBACK_TEXT } from '@/utils';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

const DEFAULT_TEXT_AREA_HEIGHT = '3em';
const CONTRACT_FLEX_CELL_RATIO = [1, 3.5];
const CONTRACT_FLEX_CELL_STYLE: Omit<TextCellType, 'value'> = {
  ...centerTextStyle,
  wrapperStyle: { padding: '0 8px', display: 'flex' },
  textStyle: { fontSize: PDF_FONT_SIZE.XL },
};
// whiteを指定してしまうとwin環境で左側で接しているボーダーが欠けているように見えてしまう。
const nullableBorderStyle = '1px solid transparent';
const PAYMENT_FIRST_CELL: ReportTableProps['data'][number]['row'][number] = {
  value: '',
  wrapperStyle: { borderTop: nullableBorderStyle },
};

type Props = {
  paymentGroups: LedgerPaymentSectionProps['paymentGroups'];
  isKsStandardAfter2025: boolean;
};

export const PaymentGroupTable: FC<Props> = ({
  paymentGroups,
  isKsStandardAfter2025,
}) => {
  const { t } = useTranslation();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  // ratioは全体で100になるように調整
  const getTopHeader = useCallback(
    <T extends boolean>(
      headerMode: T,
    ): T extends true
      ? ReportTableProps['header']
      : ReportTableProps['data'][number]['row'] => {
      const backgroundStyle = headerMode
        ? undefined
        : { backgroundColor: LEDGER_BG_COLOR };
      return [
        {
          value: t('payment-group-name'),
          colSpan: 3,
          ...(headerMode ? { ratio: 42 } : {}),
          wrapperStyle: backgroundStyle,
        },
        {
          type: 'text',
          align: 'center',
          value: t('total-expenditure'),
          ...(headerMode ? { ratio: 12 } : {}),
          wrapperStyle: backgroundStyle,
        },
        {
          type: 'text',
          align: 'center',
          value: t('payee-count'),
          ...(headerMode ? { ratio: 8 } : {}),
          wrapperStyle: backgroundStyle,
        },
        {
          value: t('payment-group-overview'),
          ...(headerMode ? { ratio: 38 } : {}),
          wrapperStyle: backgroundStyle,
        },
      ] as T extends true
        ? ReportTableProps['header']
        : ReportTableProps['data'][number]['row'];
    },
    [t],
  );

  const getContractTableData = useCallback(
    ({
      paymentType,
      contracts,
    }: {
      paymentType: PaymentType;
      contracts: z.infer<typeof PaymentModel.shape.contracts>;
    }): ReportTableProps['data'] => {
      return contracts.flatMap<ReportTableProps['data'][number]>(
        (contract, idx) => {
          const isFirst = idx === 0;
          return [
            ...(isFirst
              ? ([
                  {
                    row: [
                      PAYMENT_FIRST_CELL,
                      PAYMENT_FIRST_CELL,
                      {
                        value: t('table-payment-contract-overview-label'),
                        wrapperStyle: { backgroundColor: LEDGER_BG_COLOR },
                      },
                      {
                        type: 'text',
                        value:
                          getContractAmountOrExpendituresTitle(paymentType),
                        wrapperStyle: {
                          backgroundColor: LEDGER_BG_COLOR,
                          borderRight: LEDGER_BORDER,
                        },
                        align: 'center',
                      },
                      ...(!isKsStandardAfter2025
                        ? ([
                            {
                              type: 'text',
                              value: t('number-of-applicants-table-title'),
                              wrapperStyle: {
                                backgroundColor: LEDGER_BG_COLOR,
                              },
                              align: 'center',
                            },
                            {
                              type: 'multiple',
                              wrapperStyle: {
                                padding: 0,
                                backgroundColor: LEDGER_BG_COLOR,
                              },
                              cell: {
                                cellRatios: CONTRACT_FLEX_CELL_RATIO,
                                flexCells: [
                                  {
                                    type: 'text',
                                    align: 'center',
                                    value: t('bid-rate'),
                                    ...CONTRACT_FLEX_CELL_STYLE,
                                  },
                                  {
                                    type: 'text',
                                    align: 'center',
                                    value: t('payment-reason-title'),
                                    ...CONTRACT_FLEX_CELL_STYLE,
                                  },
                                ],
                              },
                            },
                          ] satisfies ReportTableProps['data'][number]['row'])
                        : ([
                            {
                              value: '',
                              colSpan: 2,
                              wrapperStyle: {
                                borderBottom: nullableBorderStyle,
                              },
                            },
                          ] satisfies ReportTableProps['data'][number]['row'])),
                    ],
                  },
                ] satisfies ReportTableProps['data'])
              : []),
            {
              row: [
                PAYMENT_FIRST_CELL,
                PAYMENT_FIRST_CELL,
                {
                  value: `${contract.overview}\n${
                    getContractName(
                      contract.contractMethod,
                      contract.contractMethodDescription,
                    ) ?? FALLBACK_TEXT
                  } `,
                },
                {
                  type: 'text',
                  value: paymentValueConverter(
                    contract.amount,
                    'thousand',
                    false,
                    undefined,
                  ),
                  align: 'right',
                  wrapperStyle: isKsStandardAfter2025
                    ? { borderRight: LEDGER_BORDER }
                    : undefined,
                },
                ...(!isKsStandardAfter2025
                  ? ([
                      {
                        type: 'text',
                        value: paymentNumberConverter(
                          contract.numberOfApplicants,
                        ),
                        align: 'right',
                      },
                      {
                        type: 'multiple',
                        wrapperStyle: { padding: 0 },
                        cell: {
                          cellRatios: CONTRACT_FLEX_CELL_RATIO,
                          flexCells: [
                            {
                              type: 'text',
                              align: 'right',
                              value: formatBidRate(contract.bidRate),
                              ...CONTRACT_FLEX_CELL_STYLE,
                            },
                            {
                              type: 'text',
                              value: contract.reason,
                              ...CONTRACT_FLEX_CELL_STYLE,
                            },
                          ],
                        },
                      },
                    ] satisfies ReportTableProps['data'][number]['row'])
                  : []),
              ],
            },
          ];
        },
      );
    },
    [t, isKsStandardAfter2025],
  );

  // 支出先名テーブルの取得
  const getPaymentTableData = useCallback(
    (
      paymentGroup: z.infer<typeof PaymentGroupModel.shape.payments>,
    ): ReportTableProps['data'] => {
      return paymentGroup.flatMap<ReportTableProps['data'][number]>(
        (payment) => {
          const paymentTableData = payment.contracts.length
            ? getContractTableData({
                paymentType: payment.type,
                contracts: payment.contracts,
              })
            : [];
          return [
            {
              row: [
                PAYMENT_FIRST_CELL,
                {
                  colSpan: 2,
                  value: t('payment-name'),
                  wrapperStyle: { backgroundColor: LEDGER_BG_COLOR },
                },
                {
                  type: 'text',
                  value: t('amount-of-expenditures'),
                  wrapperStyle: { backgroundColor: LEDGER_BG_COLOR },
                  align: 'center',
                },
                {
                  value: t('corporate-number'),
                  wrapperStyle: { backgroundColor: LEDGER_BG_COLOR },
                  colSpan: 2,
                },
              ],
            },
            {
              row: [
                PAYMENT_FIRST_CELL,
                {
                  value: payment.name,
                  textWrapperStyle: { minHeight: DEFAULT_TEXT_AREA_HEIGHT },
                  colSpan: 2,
                },
                {
                  type: 'text',
                  value: paymentValueConverter(
                    payment.totalContractAmount,
                    'thousand',
                    isDebugMode,
                    payment.negativeTotalContractAmountCount,
                  ),
                  align: 'right',
                },
                {
                  value: payment.corporateNumber,
                  colSpan: 2,
                },
              ],
            },
            ...paymentTableData,
          ];
        },
      );
    },
    [t, isDebugMode, getContractTableData],
  );

  const data = useMemo<ReportTableProps['data']>(() => {
    if (!paymentGroups?.length) {
      return getEmptySectionTableDataByHeader(getTopHeader(true));
    }
    return paymentGroups?.flatMap<ReportTableProps['data'][number]>(
      (paymentGroup, idx) => {
        const paymentTableData = paymentGroup.payments.length
          ? getPaymentTableData(paymentGroup.payments)
          : [];

        return [
          ...(idx === 0 ? [] : [{ row: getTopHeader(false) }]),
          {
            row: [
              {
                type: 'text',
                value: paymentGroup.displayCode,
                wrapperStyle: {
                  width: '4%',
                  borderRight: 'none',
                },
                textStyle: { textAlign: 'center' },
              },
              {
                type: 'text',
                colSpan: 2,
                value: paymentGroup.name,
                textWrapperStyle: { minHeight: DEFAULT_TEXT_AREA_HEIGHT },
              },
              {
                type: 'text',
                value: paymentValueConverter(
                  paymentGroup.totalAmount,
                  'thousand',
                  isDebugMode,
                  paymentGroup.negativeTotalAmountCount,
                ),
                align: 'right',
              },
              {
                type: 'text',
                value: paymentNumberConverter(paymentGroup.paymentCount),
                align: 'right',
              },
              {
                type: 'text',
                value: paymentGroup.overview,
              },
            ],
          },
          ...paymentTableData,
        ];
      },
    );
  }, [paymentGroups, getTopHeader, isDebugMode, getPaymentTableData]);

  return <ReportSectionItemTable header={getTopHeader(true)} data={data} />;
};
